import { useEffect, useMemo, useState } from 'react'

import { getActiveOrderCtaKind } from '../utils/activeOrders'
import { fetchReturnLabel, getReturnLabelFileNames } from '../utils/returnLabel'

import type { ActiveOrder } from '../api/activeOrdersApi'
import type { Environment } from '../App'

interface ResolvedLabel {
  url: string
  fileName: string
}

/**
 * Fills in `return_label_url` for return-registered cards the API left
 * without one, by looking for the label in storage. The card's existing rule
 * (no `return_label_url`, no QR CTA) then applies unchanged, and the CTA
 * downloads the file under its stored name.
 *
 * A URL the API did supply always wins and is never looked up.
 */
export function useReturnLabels(
  orders: ActiveOrder[],
  env: Environment,
): ActiveOrder[] {
  const [labels, setLabels] = useState<Record<string, ResolvedLabel>>({})

  const lookupKey = orders
    .filter(
      (order) =>
        !order.return_label_url &&
        getActiveOrderCtaKind(order) === 'showQrCode' &&
        getReturnLabelFileNames(order.order_id).length > 0,
    )
    .map((order) => order.order_id)
    .join('|')

  useEffect(() => {
    if (!lookupKey) return

    const controller = new AbortController()
    const objectUrls: string[] = []

    for (const orderId of lookupKey.split('|')) {
      void fetchReturnLabel(orderId, env, controller.signal).then((file) => {
        if (!file || controller.signal.aborted) return
        const url = URL.createObjectURL(file.blob)
        objectUrls.push(url)
        setLabels((prev) => ({
          ...prev,
          [orderId]: { url, fileName: file.fileName },
        }))
      })
    }

    return () => {
      controller.abort()
      for (const url of objectUrls) URL.revokeObjectURL(url)
      setLabels({})
    }
  }, [lookupKey, env])

  return useMemo(
    () =>
      orders.map((order) => {
        const label = labels[order.order_id]
        if (order.return_label_url || !label) return order
        return {
          ...order,
          return_label_url: label.url,
          return_label_download_name: label.fileName,
        }
      }),
    [orders, labels],
  )
}
